import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { findInterviewStyle, InterviewStyle } from "apis/interviewStyle";

interface WhiteLabelContextType {
  interviewStyle?: InterviewStyle;
  refetchValues: () => void;
}

const WhiteLabelContext = createContext<WhiteLabelContextType>({
  interviewStyle: undefined,
  refetchValues: () => null,
});

type ReturnType<T> = T extends true
  ? WhiteLabelContextType
  : WhiteLabelContextType | undefined;

export const useWhiteLabel = <T extends boolean>(
  isStrictlyUsedWithinContext: T
): ReturnType<T> => {
  const context = useContext(WhiteLabelContext);
  // if in strict mode, we ensure the context is not undefined
  if (!context && isStrictlyUsedWithinContext) {
    throw new Error("useInterview must be used within an WhiteLabelProvider");
  }
  // otherwise, this hook can also be used not in strict mode, in which case it is potentially undefined
  return context;
};

export const WhiteLabelProvider: React.FC<{
  teamId: string;
  children: React.ReactNode;
}> = ({ children, teamId }) => {
  const [interviewStyle, setInterviewStyle] = useState<InterviewStyle>();

  const fetchValues = useCallback(async () => {
    const style = await findInterviewStyle(teamId);
    if (style) {
      setInterviewStyle(style);
    }
  }, [teamId]);

  // Get initial values on first load
  useEffect(() => {
    fetchValues();
  }, [fetchValues]);

  const value = {
    interviewStyle,
    refetchValues: fetchValues,
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};
